// modules/recurring.js — Opakované faktúry / predplatné-paušál. Reálna SK vertikála (mesačný paušál,
// predplatné, retainer): predplatné = records riadok (module='recurring') so schedule-spec + sumou;
// generuje DUE riadky (module='recurring_due') na kadenciu. NEpridáva tabuľku — 0 fetch/0 LLM/0 DDL.
//
// TENKÁ VRSTVA nad schedule enginom (schedule/spec + expand + dueEvents). generateDue je len wrapper
// nad pendingDueEvents: engine je čistý (nič neukladá), tu perzistujeme due riadky a posúvame cursor.
//
// NAJVÄČŠÍ RISK = DVOJITÁ FAKTURÁCIA. Dve ochrany naraz (belt & suspenders):
//   1) deterministický dueEventId (scheduleId|date[Ttime]) — occurrence má stabilnú identitu.
//   2) generovanie VNÚTRI db.transaction (mutex serializuje) + ackedIds z už existujúcich due riadkov
//      + INSERT-IF-ABSENT (get pred add). 2× po sebe aj 2× súbežne → presne 1 due na occurrence.
// specHash je zámerne MIMO identity (dueEvents.js §4): edit ne-časového poľa (suma) NEre-fajruje.
//
// SUMA SNAPSHOT (money): due drží amount_cents platnú V ČASE generovania (vzor events cena-snapshot).
// Zmena sumy predplatného nemení už vygenerované due, budúce occurrences dostanú novú sumu.
// dueAmountCents berie hodnotu VÝHRADNE zo stampu due, žiadny override argument (rental endIso pasca).
//
// ŽIADNA GENERÁCIA po pauze/zrušení: paused/cancelled → generateDue no-op (cursor sa neposunie; po
// resume catchUp policy spec rozhodne o medzere). end (until/count) rieši schedule engine.
//
// ČAS: výhradne injektovaný deps.now() (ISO). Do epoch-ms instant priestoru enginu ho preklápa
// Date.parse(deps.now()) — čistý parse injektovanej hodnoty, žiadny ambientný Date.now/new Date.
import { db, newCommonFields } from '../db';
import { validateSpec, normalizeSpec } from '../schedule/spec';
import { pendingDueEvents } from '../schedule/dueEvents';
import { civilToInstant } from '../schedule/civil';

// ─── MODULE_DEF descriptor ────────────────────────────────────────────────────
export const MODULE_DEF = {
  module: 'recurring',
  label_sk: 'Opakované faktúry',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'red',
  entity: {
    base: 'record',
    custom_fields: [
      { stable_id: 'f_label', label: 'Názov paušálu', type: 'text' },
      { stable_id: 'f_amount', label: 'Suma (€)', type: 'number' },
    ],
  },
  view: { filter: { module: 'recurring' }, sort: 'created_at_desc' },
  access: { collaborator: false, max_tier_out: 1 },
  metrics: [
    { slug: 'aktivne_pausaly', reduce: 'count', filter: { module: 'recurring', status: 'active' } },
    { slug: 'due_na_fakturaciu', reduce: 'count', filter: { module: 'recurring_due', status: 'pending' } },
  ],
};

export const RECURRING_MODULE = 'recurring';
export const RECURRING_DUE_MODULE = 'recurring_due';

export const RECURRING_STATUSES = Object.freeze(['active', 'paused', 'cancelled']);
export const TERMINAL_RECURRING_STATUSES = Object.freeze(['cancelled']);
export const RECURRING_TRANSITIONS = Object.freeze({
  active: Object.freeze(['paused', 'cancelled']),
  paused: Object.freeze(['active', 'cancelled']),
  cancelled: Object.freeze([]),
});

export const DUE_STATUSES = Object.freeze(['pending', 'invoiced', 'cancelled']);
export const TERMINAL_DUE_STATUSES = Object.freeze(['invoiced', 'cancelled']);
export const DUE_TRANSITIONS = Object.freeze({
  pending: Object.freeze(['invoiced', 'cancelled']),
  invoiced: Object.freeze([]),
  cancelled: Object.freeze([]),
});

// ─── Chyby ────────────────────────────────────────────────────────────────────
export class RecurringError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = 'RecurringError';
    this.code = code;
  }
}
const fail = (code, message) => { throw new RecurringError(code, message); };

// ─── Validátory ───────────────────────────────────────────────────────────────
const isCents = (v) => Number.isInteger(v) && v >= 0;
const isNonEmpty = (v) => typeof v === 'string' && v.trim() !== '';
function assertDeps(deps) {
  if (typeof deps?.now !== 'function') fail('E_INVALID_INPUT', 'deps.now je povinná funkcia (injektovaný čas)');
}
// ISO (injektovaný deps.now) → epoch ms instant priestor enginu. Čistý parse, žiadny ambient čas.
function nowInstant(deps) {
  const ms = Date.parse(deps.now());
  if (!Number.isFinite(ms)) fail('E_INVALID_INPUT', 'deps.now() musí vrátiť valídny ISO timestamp');
  return ms;
}

async function getLiveRecurring(id, store) {
  if (!isNonEmpty(id)) fail('E_NOT_FOUND', 'Predplatné neexistuje alebo je zmazané');
  const rec = await store.get(id);
  if (!rec || rec.module !== RECURRING_MODULE || rec.deleted_at != null) {
    fail('E_NOT_FOUND', 'Predplatné neexistuje alebo je zmazané');
  }
  return rec;
}

async function getLiveDue(id, store) {
  if (!isNonEmpty(id)) fail('E_NOT_FOUND', 'Due položka neexistuje alebo je zmazaná');
  const rec = await store.get(id);
  if (!rec || rec.module !== RECURRING_DUE_MODULE || rec.deleted_at != null) {
    fail('E_NOT_FOUND', 'Due položka neexistuje alebo je zmazaná');
  }
  return rec;
}

// ─── createRecurring — nové 'active' predplatné ───────────────────────────────
// input = { spec (schedule spec, kind recurring/one_off), amount_cents (int centy ≥0),
//           counterparty_ref?, label? }
export async function createRecurring(input, deps) {
  assertDeps(deps);
  const { ok, errors } = validateSpec(input?.spec);
  if (!ok) fail('E_INVALID_INPUT', `Neplatný spec: ${errors.map((e) => `${e.path}: ${e.message}`).join('; ')}`);
  if (!isCents(input.amount_cents)) fail('E_INVALID_INPUT', 'amount_cents musí byť celé nezáporné číslo v centoch');

  const spec = normalizeSpec(input.spec);
  // catchUp 'skip' = predplatné, ktoré NIKDY nevyfakturuje: generateDue beží vždy AŽ po instante,
  // takže každá occurrence je „zmeškaná" → so 'skip' sa VŽDY potlačí (Fable C3 empirický repro).
  // Billing modul to nesmie dovoliť — 'all' (fakturuj každú) alebo 'latest_only' (kolaps na 1).
  if (spec.catchUp === 'skip') {
    fail('E_INVALID_INPUT', "catchUp 'skip' by znamenalo predplatné, ktoré nikdy nevyfakturuje — použi 'all' alebo 'latest_only'");
  }
  const anchorInstant = civilToInstant(spec.anchor.date, spec.anchor.time);
  const at = deps.now();
  const row = {
    ...newCommonFields(),
    module: RECURRING_MODULE,
    status: 'active',
    entry_date: at.slice(0, 10),
    created_at: at,
    updated_at: at,
    custom: {
      spec,
      amount_cents: input.amount_cents,
      counterparty_ref: isNonEmpty(input.counterparty_ref) ? input.counterparty_ref : null,
      label: isNonEmpty(input.label) ? input.label.trim() : null,
      cursor: anchorInstant - 1,
      status_history: [{ from: null, to: 'active', at, kind: 'create' }],
    },
  };
  await db.records.add(row);
  return row;
}

// ─── Jadro prechodu predplatného — read-guard-write v tx ──────────────────────
async function transitionRecurring(id, toStatus, meta, deps) {
  assertDeps(deps);
  return db.transaction(async (tx) => {
    const rec = await getLiveRecurring(id, tx.records);
    const from = rec.status;
    if (TERMINAL_RECURRING_STATUSES.includes(from)) fail('E_TERMINAL_STATUS', `Status '${from}' je terminálny`);
    if (!(RECURRING_TRANSITIONS[from] || []).includes(toStatus)) {
      fail('E_INVALID_TRANSITION', `Prechod '${from}'→'${toStatus}' nie je definovaná hrana`);
    }
    const at = deps.now();
    const entry = { from, to: toStatus, at, kind: 'transition' };
    if (isNonEmpty(meta?.note)) entry.note = meta.note;
    const custom = {
      ...(rec.custom || {}),
      status_history: [...(rec.custom?.status_history || []), entry],
    };
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { status: toStatus, custom, updated_at: at, rev });
    return { ...rec, status: toStatus, custom, updated_at: at, rev };
  });
}

export async function pauseRecurring(id, meta = {}, deps) {
  return transitionRecurring(id, 'paused', meta, deps);
}
export async function resumeRecurring(id, meta = {}, deps) {
  return transitionRecurring(id, 'active', meta, deps);
}
export async function cancelRecurring(id, meta = {}, deps) {
  return transitionRecurring(id, 'cancelled', meta, deps);
}

// ─── generateDue — idempotentné generovanie due riadkov (money-critical, v TX) ─
// Vráti { generated: [due...], suppressed: [...], subscription }. Idempotent: 2× po sebe aj súbežne
// = presne 1 due na occurrence. Paused/cancelled → no-op (generated: []).
export async function generateDue(subscriptionId, deps) {
  assertDeps(deps);
  const asOf = nowInstant(deps);

  return db.transaction(async (tx) => {
    const sub = await getLiveRecurring(subscriptionId, tx.records);
    if (sub.status !== 'active') {
      return { generated: [], suppressed: [], subscription: sub }; // paused/cancelled → nič negeneruj
    }

    const spec = sub.custom?.spec;
    const cursor = sub.custom?.cursor;
    if (!spec || typeof cursor !== 'number') fail('E_CORRUPT', 'Predplatné nemá platný spec/cursor');

    // ackedIds = už existujúce due riadky tohto predplatného → engine ich odfiltruje (dedupe).
    const existingDue = await tx.records
      .filter((r) => r != null && r.module === RECURRING_DUE_MODULE && r.deleted_at == null
        && r.custom?.subscription_ref === subscriptionId)
      .toArray();
    const ackedIds = new Set(existingDue.map((r) => r.id));

    const { events, suppressed, nextCursor } = pendingDueEvents({
      scheduleId: subscriptionId,
      module: RECURRING_DUE_MODULE,
      spec,
      payload: {},
      cursor,
      asOf,
      ackedIds,
      calendar: deps.calendar, // undefined OK keď businessDayAdjust 'none'
    });

    const at = deps.now();
    // Skorumpovaná suma (float/negatívne cez sync/edit) → E_CORRUPT NAHLAS, nefabrikuj 0-centovú
    // faktúru (Fable B2 empirický repro: tichá nulová faktúra = tichá strata príjmu). Konzistentné
    // so spec/cursor E_CORRUPT guardom vyššie — validáciu robí aj createRecurring, toto chráni pred
    // korupciou PO vytvorení.
    if (!isCents(sub.custom?.amount_cents)) fail('E_CORRUPT', 'Predplatné má nevalídnu amount_cents (nie celé centy)');
    const amountSnapshot = sub.custom.amount_cents;
    const generated = [];
    for (const ev of events) {
      // INSERT-IF-ABSENT: deterministické id → aj pri hypotetickom race druhý add nezdvojí.
      // eslint-disable-next-line no-await-in-loop
      const clash = await tx.records.get(ev.id);
      if (clash) continue; // už existuje (acked by mal chytiť, toto je poistka)
      const row = {
        ...newCommonFields(),
        id: ev.id, // deterministický dueEventId — prepíše uuid
        module: RECURRING_DUE_MODULE,
        status: 'active', // živosť riadku (due stav je v custom.status)
        entry_date: ev.occurrence.date,
        created_at: at,
        updated_at: at,
        custom: {
          subscription_ref: subscriptionId,
          occurrence: { date: ev.occurrence.date, time: ev.occurrence.time ?? null },
          amount_cents: amountSnapshot, // SNAPSHOT v čase generovania
          spec_hash: ev.specHash,
          counterparty_ref: sub.custom?.counterparty_ref ?? null,
          status: 'pending',
          emitted_because: ev.emittedBecause,
          status_history: [{ from: null, to: 'pending', at, kind: 'generate' }],
        },
      };
      // eslint-disable-next-line no-await-in-loop
      await tx.records.add(row);
      generated.push(row);
    }

    // Posuň cursor (nikdy dozadu — engine to garantuje). Ulož aj keď 0 generated (cursor postúpi).
    const rev = (sub.rev || 1) + 1;
    const custom = { ...(sub.custom || {}), cursor: nextCursor };
    await tx.records.update(subscriptionId, { custom, updated_at: at, rev });

    return { generated, suppressed, subscription: { ...sub, custom, updated_at: at, rev } };
  });
}

// ─── Jadro prechodu due — read-guard-write v tx ───────────────────────────────
async function transitionDue(id, toStatus, meta, deps) {
  assertDeps(deps);
  return db.transaction(async (tx) => {
    const rec = await getLiveDue(id, tx.records);
    const from = rec.custom?.status;
    if (!DUE_STATUSES.includes(from)) fail('E_INVALID_INPUT', `Perzistovaný due status '${from}' nie je platný`);
    if (TERMINAL_DUE_STATUSES.includes(from)) fail('E_TERMINAL_STATUS', `Due status '${from}' je terminálny`);
    if (!(DUE_TRANSITIONS[from] || []).includes(toStatus)) {
      fail('E_INVALID_TRANSITION', `Prechod '${from}'→'${toStatus}' nie je definovaná hrana`);
    }
    const at = deps.now();
    const entry = { from, to: toStatus, at, kind: 'transition' };
    if (isNonEmpty(meta?.note)) entry.note = meta.note;
    if (isNonEmpty(meta?.invoice_ref)) entry.invoice_ref = meta.invoice_ref;
    const custom = {
      ...(rec.custom || {}),
      status: toStatus,
      invoice_ref: toStatus === 'invoiced' && isNonEmpty(meta?.invoice_ref) ? meta.invoice_ref : (rec.custom?.invoice_ref ?? null),
      status_history: [...(rec.custom?.status_history || []), entry],
    };
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { custom, updated_at: at, rev });
    return { ...rec, custom, updated_at: at, rev };
  });
}

// Fakturovaná due (napojenie na skutočnú faktúru cez meta.invoice_ref).
export async function markDueInvoiced(id, meta = {}, deps) {
  return transitionDue(id, 'invoiced', meta, deps);
}
export async function cancelDue(id, meta = {}, deps) {
  return transitionDue(id, 'cancelled', meta, deps);
}

// ─── Čisté funkcie ────────────────────────────────────────────────────────────

// Živá due daného predplatného (nezmazaná, správny modul + subscription_ref).
const isLiveDueFor = (r, subId) =>
  r != null && r.module === RECURRING_DUE_MODULE && r.deleted_at == null && r.custom?.subscription_ref === subId;

export function dueRecords(records, subId) {
  const recs = Array.isArray(records) ? records : [];
  return recs.filter((r) => isLiveDueFor(r, subId));
}

export function pendingDueRecords(records, subId) {
  return dueRecords(records, subId).filter((r) => r.custom?.status === 'pending');
}

// Suma due — VÝHRADNE zo snapshotnutého amount_cents (stamp pri generovaní). Nie pending → null
// (fakturovaná už je v účtovníctve, zrušená nič nedlží). Žiadny override argument (rental endIso pasca).
export function dueAmountCents(due) {
  if (due == null || due.deleted_at != null) return null;
  const c = due.custom;
  if (!c || c.status !== 'pending') return null;
  return isCents(c.amount_cents) ? c.amount_cents : null;
}

// Nefakturovaná suma predplatného = Σ pending due (honest: invoiced/cancelled nerátajú).
export function totalPendingCents(records, subId) {
  let cents = 0;
  for (const r of pendingDueRecords(records, subId)) {
    if (isCents(r.custom?.amount_cents)) cents += r.custom.amount_cents;
  }
  return cents;
}
